import { useEffect, useRef } from 'react';

import { AGGREGATE_NAMES, CHAT_COMMANDS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { ApplicantSwitcher } from './ApplicantSwitcher';
import { ChatHeader } from './ChatHeader';
import { ChatNumberBanner } from './ChatNumberBanner';
import { Composer } from './Composer';
import { WindowBanner } from './WindowBanner';
import Spinner from '~/components/icons/Spinner';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { MessageStream } from '~/features/whatsapp/components/MessageStream';
import { DEFAULT_MESSAGE_PAGE_SIZE } from '~/features/whatsapp/constants';
import { useChatMessages } from '~/features/whatsapp/hooks/useChatMessages';
import { useChats } from '~/features/whatsapp/hooks/useChats';
import { useChatSocket } from '~/features/whatsapp/hooks/useChatSocket';
import { useMarkChatViewedMutation } from '~/features/whatsapp/hooks/useMarkChatViewedMutation';
import { closePanel } from '~/features/whatsapp/stores/chatPanelStore';
import { deriveWindowState } from '~/features/whatsapp/utils/deriveChatStatus';
import { findLastInboundMessage } from '~/features/whatsapp/utils/findLastInboundMessage';
import type { ApplicantFilter } from '~/features/whatsapp/utils/resolveApplicantChats';
import { resolveSenderUnavailable } from '~/features/whatsapp/utils/resolveSenderUnavailable';

type Props = {
    chatId: string;
    displayName: string;
    loanRequestIds: Array<string>;
    showApplicantSwitcher: boolean;
    activeApplicantFilter: ApplicantFilter;
    onApplicantFilterChange: (filter: ApplicantFilter) => void;
};

export const SingleChatPane = ({
    activeApplicantFilter,
    chatId,
    displayName,
    loanRequestIds,
    onApplicantFilterChange,
    showApplicantSwitcher,
}: Props) => {
    const { t } = useTranslation();
    const { data: chats } = useChats();
    const chat = chats?.find((entry) => entry.id === chatId);

    const { fetchNextPage, hasNextPage, isError, isFetchingNextPage, isLoading, messages } = useChatMessages(
        chatId,
        DEFAULT_MESSAGE_PAGE_SIZE,
    );

    const markViewed = useMarkChatViewedMutation(chatId);
    const lastMarkedMessageIdRef = useRef<string | null>(null);
    const lastSequenceRef = useRef<number | null>(null);

    const { permissions } = useAclPermissions(
        chat
            ? {
                  actionFilter: {
                      [AGGREGATE_NAMES.CHAT]: [CHAT_COMMANDS.linkChat, CHAT_COMMANDS.unlinkChat],
                  },
                  chatLinkInfo: {
                      chatId,
                      loanRequestIds: chat.loanRequestIds,
                      partnerIds: chat.partnerIds,
                      ...(chat.phone && { phone: chat.phone }),
                      phoneNumberId: chat.phoneNumberId,
                  },
              }
            : undefined,
    );
    const canLink = permissions.commands[AGGREGATE_NAMES.CHAT][CHAT_COMMANDS.linkChat];

    useEffect(() => {
        const lastMessage = messages.at(-1);
        lastSequenceRef.current = lastMessage?.sequence ?? null;
    }, [messages]);

    useChatSocket(chatId, {
        lastSequenceRef,
        onForbidden: () => {
            toast.error(t('WHATSAPP_CHAT.CHAT.ACCESS_REVOKED'));
            closePanel();
        },
    });

    useEffect(() => {
        const lastInbound = findLastInboundMessage(messages);
        if (!lastInbound) return;
        const { messageId } = lastInbound;
        if (lastMarkedMessageIdRef.current === messageId) return;
        markViewed.mutate(messageId, {
            onSuccess: () => {
                lastMarkedMessageIdRef.current = messageId;
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages]);

    if (!chat) {
        return (
            <div className="flex h-full items-center justify-center">
                <Spinner />
            </div>
        );
    }

    return (
        <div className="flex h-full min-h-0 flex-col">
            <ChatHeader
                canLink={canLink}
                chatId={chatId}
                displayName={displayName}
                lastInboundAt={chat.lastInboundAt}
                loanRequestIds={loanRequestIds}
                resolved={chat.resolved}
            />
            {showApplicantSwitcher && (
                <ApplicantSwitcher activeFilter={activeApplicantFilter} onChange={onApplicantFilterChange} />
            )}
            <WindowBanner lastInboundAt={chat.lastInboundAt} />
            <ChatNumberBanner chat={chat} senderUnavailable={resolveSenderUnavailable({ chat, messages })} />
            <div className="min-h-0 flex-1 overflow-y-auto">
                {isLoading && (
                    <div className="flex items-center justify-center py-10">
                        <Spinner />
                    </div>
                )}
                {isError && (
                    <div className="px-4 py-4 text-sm text-alert-error">
                        {t('WHATSAPP_CHAT.ERROR.LOAD_MESSAGES_FAILED')}
                    </div>
                )}
                {messages.length > 0 && (
                    <MessageStream
                        chatId={chatId}
                        messages={messages}
                        hasOlderMessages={Boolean(hasNextPage)}
                        isLoadingOlder={isFetchingNextPage}
                        onLoadOlder={() => {
                            if (hasNextPage && !isFetchingNextPage) fetchNextPage().catch(() => undefined);
                        }}
                    />
                )}
            </div>
            <Composer chatId={chatId} windowState={deriveWindowState(chat.lastInboundAt)} />
        </div>
    );
};
